import { ConsentStatus, SpokenLanguage } from "@repo/db/client";
import {
	Button,
	Flex,
	Icon,
	Input,
	Link,
	PopoverTextArea,
	Select,
	Spacer,
	Text,
	useTheme,
	WithTooltip,
} from "@repo/ui";
import { sortBy } from "@repo/utils/objectUtils";
import { useState } from "react";
import { useLocation } from "wouter";
import { useApiClient } from "../contexts/ApiContext";
import { useEntity, useQuery, useTransaction } from "../db/DatabaseProvider";
import { Insurance, Patient, PatientId, TaskId } from "../db/schema";
import { getTenantSlug, TENANT_SPRUCE_ORG_IDS } from "../tenant";
import { ConfirmDialog } from "../features/providers/components/ProviderNestedModal/ConfirmDialog";
import { EditInsuranceModal } from "./EditInsuranceModal";
import { InsuranceCardImageModal } from "./InsuranceCardImageModal";

type PatientCardProps = {
	taskId: TaskId;
};

type ValidationResult = {
	ok: boolean;
	error?: string;
};

const validatePhone = (phone: string): ValidationResult => {
	if (!phone.trim()) return { ok: true }; // Empty is ok
	const cleanPhone = phone.replace(/\D/g, "");
	if (cleanPhone.length !== 10) {
		return { ok: false, error: "Phone must be 10 digits" };
	}
	return { ok: true };
};

const validateDOB = (dateStr: string): ValidationResult => {
	if (!dateStr.trim()) return { ok: true }; // Empty is ok
	const date = new Date(dateStr);
	if (isNaN(date.getTime())) {
		return { ok: false, error: "Invalid date format" };
	}
	if (date > new Date()) {
		return { ok: false, error: "Birth date cannot be in the future" };
	}
	return { ok: true };
};

const validateRequired = (
	value: string,
	fieldName: string,
): ValidationResult => {
	if (!value.trim()) {
		return { ok: false, error: `${fieldName} is required` };
	}
	return { ok: true };
};

const calculateAge = (birthday: Date | undefined): number | undefined => {
	if (!birthday) return undefined;
	const today = new Date();
	const birthDate = new Date(birthday);
	let age = today.getFullYear() - birthDate.getFullYear();
	const monthDiff = today.getMonth() - birthDate.getMonth();
	if (
		monthDiff < 0 ||
		(monthDiff === 0 && today.getDate() < birthDate.getDate())
	) {
		age--;
	}
	return age;
};

const formatDate = (date: Date | undefined): string => {
	if (!date) return "";
	return date.toLocaleDateString("en-US", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	});
};

const formatDateForInput = (date: Date | undefined): string => {
	if (!date) return "";
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

type ImageData = {
	key: string;
	signedUrl: string;
	expiresAt: Date;
};

export const PatientCard: React.FC<PatientCardProps> = ({ taskId }) => {
	const theme = useTheme();
	const [_, navigate] = useLocation();
	const [editingField, setEditingField] = useState<string | undefined>();
	const [validationErrors, setValidationErrors] = useState<
		Record<string, string>
	>({});

	const [imageModalData, setImageModalData] = useState<{
		images: ImageData[];
		insuranceName: string;
		isOpen: boolean;
	}>({ images: [], insuranceName: "", isOpen: false });
	const [_loadingImages, setLoadingImages] = useState(false);
	const [editInsuranceModal, setEditInsuranceModal] = useState<{
		insurance: Insurance | null;
		isOpen: boolean;
		isSaving: boolean;
	}>({ insurance: null, isOpen: false, isSaving: false });
	const [addInsuranceModal, setAddInsuranceModal] = useState<{
		isOpen: boolean;
		isSaving: boolean;
	}>({ isOpen: false, isSaving: false });
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	const apiClient = useApiClient();

	const currentTask = useEntity("task", taskId);
	const patient = useEntity("patient", currentTask?.patient);

	const updatePatient = useTransaction(
		(tx, patientId: PatientId, updates: Partial<Patient>) => {
			tx.update("patient", patientId, (current) => ({
				...current,
				...updates,
			}));
		},
	);

	const patientTasks =
		useQuery(
			"task",
			currentTask
				? (q) => q.where("patient", "=", currentTask.patient)
				: undefined,
			[currentTask?.patient],
		) || [];

	const otherTasks = patientTasks.filter((t) => t.id !== taskId);

	const handleViewInsuranceImages = async (insurance: Insurance) => {
		if (
			!insurance.patientInsuranceId ||
			!insurance.imageUrls ||
			insurance.imageUrls.length === 0
		) {
			return;
		}

		setLoadingImages(true);
		try {
			const result = await apiClient.tools.generateMediaSignedUrls({
				bucketName: "patient-insurance-cards",
				mediaKeys: insurance.imageUrls,
			});

			console.log("signedUrlResult", result);

			setImageModalData({
				images: result.signedUrls.map((url) => ({
					key: url.key,
					signedUrl: url.signedUrl,
					expiresAt: new Date(url.expiresAt),
				})),
				insuranceName: insurance.planName ?? insurance.payor ?? "Unknown",
				isOpen: true,
			});
		} catch (error) {
			console.error("Failed to generate signed URLs:", error);
			// Could add toast notification here
		} finally {
			setLoadingImages(false);
		}
	};

	const handleEditInsurance = (insurance: Insurance) => {
		setEditInsuranceModal({
			insurance,
			isOpen: true,
			isSaving: false,
		});
	};

	const updateInsurance = useTransaction(
		(
			tx,
			patientId: PatientId,
			insuranceId: string,
			updates: Partial<Insurance>,
		) => {
			tx.update("patient", patientId, (current) => ({
				...current,
				insurance: current.insurance.map((ins) =>
					ins.id === insuranceId ? { ...ins, ...updates } : ins,
				),
			}));
		},
	);

	const handleSaveInsuranceEdit = async (data: {
		payor: string;
		memberId: string;
		planType: string | undefined;
		planName: string | undefined;
		specialistRefsReqPriorAuth: boolean | undefined;
		payorPhoneNumber: string | undefined;
	}) => {
		if (!patient || !editInsuranceModal.insurance) return;

		setEditInsuranceModal((prev) => ({ ...prev, isSaving: true }));

		const insuranceId = editInsuranceModal.insurance.id;

		try {
			const result = await apiClient.tools.updatePatientInsurance({
				patientId: patient.id,
				patientInsuranceId: editInsuranceModal.insurance.patientInsuranceId!,
				payor: data.payor,
				memberId: data.memberId,
				planType: data.planType,
				planName: data.planName,
				specialistRefsReqPriorAuth: data.specialistRefsReqPriorAuth,
				payorPhoneNumber: data.payorPhoneNumber,
			});

			if (result.success) {
				updateInsurance(patient.id, insuranceId, {
					payor: data.payor,
					memberId: data.memberId,
					type: data.planType,
					planName: data.planName,
					specialistRefsReqPriorAuth: data.specialistRefsReqPriorAuth,
					phone: data.payorPhoneNumber,
				});

				setEditInsuranceModal({
					insurance: null,
					isOpen: false,
					isSaving: false,
				});
			} else {
				console.error("Failed to update insurance:", result.message);
				setEditInsuranceModal((prev) => ({ ...prev, isSaving: false }));
			}
		} catch (error) {
			console.error("Failed to update insurance:", error);
			setEditInsuranceModal((prev) => ({ ...prev, isSaving: false }));
		}
	};

	const addInsurance = useTransaction(
		(tx, patientId: PatientId, newInsurance: Insurance) => {
			tx.update("patient", patientId, (current) => ({
				...current,
				insurance: [...current.insurance, newInsurance],
			}));
		},
	);

	const handleSaveInsuranceAdd = async (data: {
		payor: string;
		memberId: string;
		planType: string | undefined;
		planName: string | undefined;
		specialistRefsReqPriorAuth: boolean | undefined;
		payorPhoneNumber: string | undefined;
	}) => {
		if (!patient) return;

		setAddInsuranceModal({ isOpen: true, isSaving: true });

		try {
			const result = await apiClient.tools.createPatientInsurance({
				patientId: patient.id,
				payor: data.payor,
				memberId: data.memberId,
				planType: data.planType,
				planName: data.planName,
				specialistRefsReqPriorAuth: data.specialistRefsReqPriorAuth,
				payorPhoneNumber: data.payorPhoneNumber,
				isPrimary: patient.insurance.length === 0,
			});

			if (result.success && result.patientInsurance && result.insurance) {
				const newInsurance: Insurance = {
					id: result.patientInsurance.id,
					patientInsuranceId: result.patientInsurance.id,
					memberId: result.patientInsurance.insuranceMemberId ?? "",
					type: result.insurance.planType ?? undefined,
					planName: result.insurance.planName ?? undefined,
					payor: result.insurance.payor ?? undefined,
					phone: result.insurance.payorPhoneNumber ?? undefined,
					isPrimary: result.patientInsurance.isPrimary ?? false,
					isExpired: result.patientInsurance.isExpired ?? false,
					specialistRefsReqPriorAuth:
						result.insurance.specialistRefsReqPriorAuth ?? undefined,
					imageUrls: undefined,
					address: undefined,
				};

				addInsurance(patient.id, newInsurance);
				setAddInsuranceModal({ isOpen: false, isSaving: false });
			} else {
				console.error("Failed to create insurance:", result.message);
				setAddInsuranceModal({ isOpen: true, isSaving: false });
			}
		} catch (error) {
			console.error("Failed to create insurance:", error);
			setAddInsuranceModal({ isOpen: true, isSaving: false });
		}
	};

	const deleteInsurance = useTransaction(
		(tx, patientId: PatientId, insuranceId: string) => {
			tx.update("patient", patientId, (current) => ({
				...current,
				insurance: current.insurance.filter((ins) => ins.id !== insuranceId),
			}));
		},
	);

	const handleDeleteInsurance = async () => {
		if (!patient || !editInsuranceModal.insurance) return;

		setEditInsuranceModal((prev) => ({ ...prev, isSaving: true }));

		const insuranceToDelete = editInsuranceModal.insurance;

		try {
			const result = await apiClient.tools.deletePatientInsurance({
				patientId: patient.id,
				patientInsuranceId: insuranceToDelete.patientInsuranceId!,
			});

			if (result.success) {
				deleteInsurance(patient.id, insuranceToDelete.id);
				setEditInsuranceModal({
					insurance: null,
					isOpen: false,
					isSaving: false,
				});
			} else {
				console.error("Failed to delete insurance:", result.message);
				setEditInsuranceModal((prev) => ({ ...prev, isSaving: false }));
			}
		} catch (error) {
			console.error("Failed to delete insurance:", error);
			setEditInsuranceModal((prev) => ({ ...prev, isSaving: false }));
		}
	};

	const handleFieldUpdate = (field: keyof Patient, value: any) => {
		if (!patient) return;

		let validation: ValidationResult = { ok: true };

		// Validate based on field type
		switch (field) {
			case "name":
				validation = validateRequired(value, "Name");
				break;
			case "phoneNumber":
				validation = validatePhone(value);
				break;
			case "dateOfBirth":
				validation = validateDOB(value);
				if (validation.ok && value) {
					// Parse date components to avoid timezone issues
					const [year, month, day] = value.split("-").map(Number);
					const date = new Date(year, month - 1, day, 12, 0, 0, 0);
					value = date;
				}
				break;
			case "consentRequestStatus":
				if (value === "true") value = true;
				else if (value === "false") value = false;
				else if (value === "") value = null;
				validation = { ok: true };
				break;
			case "responsiblePartyName":
				// Handle "Self" case - convert to undefined for backend
				if (value === "Self" || value === "") {
					value = undefined;
				}
				validation = { ok: true };
				break;
		}

		if (validation.ok) {
			setValidationErrors((prev) => ({ ...prev, [field]: "" }));
			// Convert empty strings to undefined for optional fields
			if (field !== "name" && value === "") {
				value = undefined;
			}
			updatePatient(patient.id, { [field]: value });
			setEditingField(undefined);
		} else {
			// Exit editing mode on validation failure
			setValidationErrors((prev) => ({
				...prev,
				[field]: validation.error ?? "",
			}));
			setEditingField(undefined);
		}
	};

	const startEditing = (field: keyof Patient) => {
		setEditingField(field);
	};

	if (!patient) return null;

	const age = calculateAge(patient.dateOfBirth);
	const dobFormatted = formatDate(patient.dateOfBirth);

	return (
		<Flex
			column
			gap="xl"
			padding="md"
			borderRadius="md"
			background="sand1"
			shadow="subtle"
		>
			{/* Header with patient name and avatar */}
			<Flex align="center" justify="space-between" gap="xl">
				<Flex column gap="xxs">
					{editingField === "name" ? (
						<Input
							defaultValue={patient.name || ""}
							onBlur={(e) => handleFieldUpdate("name", e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter")
									handleFieldUpdate("name", e.currentTarget.value);
								if (e.key === "Escape") setEditingField(undefined);
							}}
							size="lg"
							weight="semibold"
							style={{
								border: validationErrors.name ? "1px solid red" : undefined,
								minWidth: "200px",
							}}
							autoFocus
						/>
					) : (
						<div
							style={{ cursor: "pointer" }}
							onClick={() => startEditing("name")}
						>
							<Text size="lg" weight="semibold" color="sand12">
								{patient.name || (
									<Text size="lg" weight="semibold" color="sand9">
										Patient name
									</Text>
								)}
							</Text>
						</div>
					)}
					<Flex gap="md" align="center" wrap>
						{/* Birthday */}
						{editingField === "dateOfBirth" ? (
							<input
								type="date"
								defaultValue={formatDateForInput(patient.dateOfBirth)}
								onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
									handleFieldUpdate("dateOfBirth", e.target.value)
								}
								onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
									if (e.key === "Enter")
										handleFieldUpdate("dateOfBirth", e.currentTarget.value);
									if (e.key === "Escape") setEditingField(undefined);
								}}
								style={{
									fontSize: "14px",
									padding: "4px",
									border: validationErrors.dateOfBirth
										? "1px solid red"
										: "1px solid #ccc",
									borderRadius: "4px",
									background: "transparent",
								}}
								autoFocus
							/>
						) : (
							<div
								style={{
									cursor: "pointer",
								}}
								onClick={() => startEditing("dateOfBirth")}
							>
								<Text size="sm" color={dobFormatted ? "sand11" : "sand9"}>
									{dobFormatted || "Date of birth"}
								</Text>
							</div>
						)}

						{age && (
							<Text size="sm" color="sand11">
								({age}yo)
							</Text>
						)}
					</Flex>
				</Flex>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(2, minmax(0, 180px))",
						gridTemplateRows: "1fr 1fr",
						gap: theme.spacing["xs"],
					}}
				>
					<Flex align="center" gap="xs">
						<Icon name="Globe" size="sm" color="sand11" shrink={0} />
						<Select
							size="sm"
							variant="ghost"
							aria-label="Primary language"
							value={patient.primaryLanguage || ""}
							onSelectionChange={(value) =>
								handleFieldUpdate("primaryLanguage", value)
							}
							options={[
								{ value: undefined, label: "Select language" },
								{ value: SpokenLanguage.English, label: "English" },
								{ value: SpokenLanguage.Spanish, label: "Spanish" },
							]}
							placeholder="Select language"
							style={{ flex: 1 }}
						/>
					</Flex>
					{/* Address */}
					<Flex align="center" gap="xs">
						<Icon name="MapPin" size="sm" color="sand11" shrink={0} />
						<PopoverTextArea
							value={patient.address || ""}
							onSave={(value) => handleFieldUpdate("address", value)}
							placeholder="Address"
							style={{ flex: 1, minWidth: 0 }}
						/>
					</Flex>
					{/* SMS Consent */}
					<Flex align="center" gap="xs">
						<Icon name="BotMessageSquare" size="sm" color="sand11" shrink={0} />
						<Select
							size="sm"
							variant="ghost"
							aria-label="Text message consent"
							value={patient.consentRequestStatus}
							onSelectionChange={(value) => {
								handleFieldUpdate("consentRequestStatus", value);
							}}
							options={[
								{ value: undefined, label: "Unknown" },
								{ value: ConsentStatus.Consented, label: "Consented" },
								{ value: ConsentStatus.Declined, label: "Declined" },
							]}
							placeholder="SMS Consent"
							style={{ flex: 1 }}
						/>
					</Flex>

					{/* PCP */}
					<Flex align="center" gap="md">
						<Icon name="Stethoscope" size="sm" color="sand11" shrink={0} />
						{patient.primaryProvider?.name ? (
							<Text size="sm">{patient.primaryProvider.name}</Text>
						) : (
							<Text size="sm" light select={false}>
								Unknown PCP
							</Text>
						)}
						{patient.pcpValidatedAt && (
							<WithTooltip
								content={`Validated ${formatDate(patient.pcpValidatedAt)}`}
							>
								<Icon
									name="BadgeCheck"
									size="sm"
									fill="green11"
									color="sand1"
									shrink={0}
									focusable
								/>
							</WithTooltip>
						)}
					</Flex>
				</div>
			</Flex>

			{/* Contact Information - Compact 2-Column Layout */}
			<Flex column gap="xs">
				<Flex gap="lg">
					{/* Left side - Responsible Party and Phone */}
					<Flex column gap="xs" style={{ flex: 1, minWidth: 0 }}>
						{/* Responsible Party */}
						<Flex align="center" gap="xs">
							<Icon name="UserPen" size="sm" color="sand11" shrink={0} />
							<Text size="sm" light weight="medium">
								Responsible Party:
							</Text>
							{editingField === "responsiblePartyName" ? (
								<input
									defaultValue={patient.responsiblePartyName || "Self"}
									onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
										handleFieldUpdate("responsiblePartyName", e.target.value)
									}
									onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
										if (e.key === "Enter")
											handleFieldUpdate(
												"responsiblePartyName",
												e.currentTarget.value,
											);
										if (e.key === "Escape") setEditingField(undefined);
									}}
									placeholder="Responsible Party"
									style={{
										fontSize: "14px",
										padding: "4px",
										border: validationErrors.responsiblePartyName
											? "1px solid red"
											: "1px solid #ccc",
										borderRadius: "4px",
										background: "transparent",
									}}
									autoFocus
								/>
							) : (
								<div
									style={{
										cursor: "pointer",
									}}
									onClick={() => startEditing("responsiblePartyName")}
								>
									<Text
										size="sm"
										color={patient.responsiblePartyName ? "sand12" : "sand9"}
									>
										{patient.responsiblePartyName || "Self"}
									</Text>
								</div>
							)}
						</Flex>

						{/* Phone Number */}
						<Flex align="center" gap="xs">
							<Icon name="Phone" size="sm" color="sand11" shrink={0} />
							<Text size="sm" light weight="medium">
								Phone:
							</Text>
							{editingField === "phoneNumber" ? (
								<input
									type="tel"
									pattern="^\d{10}$"
									defaultValue={patient.phoneNumber || ""}
									onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
										handleFieldUpdate("phoneNumber", e.target.value)
									}
									onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
										if (e.key === "Enter")
											handleFieldUpdate("phoneNumber", e.currentTarget.value);
										if (e.key === "Escape") setEditingField(undefined);
									}}
									placeholder="Phone"
									style={{
										fontSize: "14px",
										padding: "4px",
										border: validationErrors.phoneNumber
											? "1px solid red"
											: "1px solid #ccc",
										borderRadius: "4px",
										background: "transparent",
									}}
									autoFocus
								/>
							) : (
								<>
									<div
										style={{
											cursor: "pointer",
										}}
										onClick={() => startEditing("phoneNumber")}
									>
										<Text
											size="sm"
											color={patient.phoneNumber ? "sand12" : "sand9"}
										>
											{patient.phoneNumber || "Phone"}
										</Text>
									</div>
									{(() => {
										const tenantSlug = getTenantSlug();
										const spruceOrgId = tenantSlug
											? TENANT_SPRUCE_ORG_IDS[tenantSlug]
											: undefined;
										return (
											patient.spruceContactId &&
											spruceOrgId && (
												<>
													<Spacer width="xs" />
													<Link
														href={`https://app.sprucehealth.com/org/${spruceOrgId}/contact/${patient.spruceContactId}`}
														target="_blank"
														rel="noopener noreferrer"
														size="sm"
														color="blue11"
													>
														See in Spruce
													</Link>
												</>
											)
										);
									})()}
								</>
							)}
						</Flex>
					</Flex>
				</Flex>
			</Flex>

			{/* Insurance - Compact Display */}
			<Flex column gap="xs">
				<Flex align="center" justify="space-between">
					<Text size="sm" weight="medium" color="sand11">
						Insurance
					</Text>
					<WithTooltip content="Add insurance">
						<Button
							variant="ghost"
							size="xs"
							onPress={() =>
								setAddInsuranceModal({ isOpen: true, isSaving: false })
							}
						>
							<Icon name="Plus" size="xs" color="sand11" />
						</Button>
					</WithTooltip>
				</Flex>
				{patient.insurance.length > 0 ? (
					<Flex column gap="xs">
						{sortBy(patient.insurance, [
							(insurance) => (insurance.isPrimary ? 0 : 1),
							"asc",
						]).map((insurance) => {
							const hasImages =
								insurance.imageUrls &&
								insurance.imageUrls.length > 0 &&
								insurance.patientInsuranceId;
							return (
								<Flex
									key={insurance.id}
									align="start"
									gap="xs"
									padding="xs"
									borderRadius="sm"
									background="sand2"
								>
									<Flex padding="xxs">
										<Icon
											name="FileHeart"
											size="sm"
											color="blue11"
											shrink={0}
										/>
									</Flex>
									<Flex column gap="xxs" style={{ flex: 1 }}>
										{/* First line: Plan name and payor */}
										<Flex align="center" gap="xs">
											{insurance.planName && (
												<Text size="sm" color="sand12">
													{insurance.planName}
												</Text>
											)}
											{insurance.payor && (
												<Text size="sm" weight="medium" color="blue11">
													{insurance.payor}
												</Text>
											)}
											{insurance.type && (
												<Flex
													padding="xxxs"
													borderRadius="sm"
													background="blue3"
												>
													<Text size="sm" weight="medium" color="blue11">
														{insurance.type}
													</Text>
												</Flex>
											)}
											{/* Primary badge - vertically centered */}
											{insurance.isPrimary && (
												<Flex
													padding="xxxs"
													borderRadius="sm"
													background="amber3"
												>
													<Text size="sm" weight="medium" color="amber11">
														PRIMARY
													</Text>
												</Flex>
											)}
											{/* Expired badge */}
											{insurance.isExpired && (
												<Flex
													padding="xxxs"
													borderRadius="sm"
													background="red3"
												>
													<Text size="sm" weight="medium" color="red11">
														EXPIRED
													</Text>
												</Flex>
											)}

											{hasImages && (
												<Flex
													align="center"
													gap="xxxs"
													padding="xxxs"
													borderRadius="sm"
													background="blue3"
													style={{ cursor: "pointer" }}
													onClick={() => handleViewInsuranceImages(insurance)}
												>
													<Icon
														name="Image"
														size="xs"
														color="blue11"
														shrink={0}
													/>
													<Text size="sm" color="blue11">
														{insurance.imageUrls?.length} image
														{insurance.imageUrls?.length !== 1 ? "s" : ""}
													</Text>
												</Flex>
											)}
											<Spacer grow />
											{insurance.patientInsuranceId && (
												<WithTooltip content="Edit insurance">
													<Button
														variant="ghost"
														size="xs"
														onPress={() => handleEditInsurance(insurance)}
													>
														<Icon name="SquarePen" size="xs" color="sand11" />
													</Button>
												</WithTooltip>
											)}
										</Flex>

										{insurance.memberId && (
											<Flex align="center" gap="xs">
												<Text size="sm" weight="medium" light>
													ID
												</Text>
												<Text size="sm" light>
													{insurance.memberId}
												</Text>
											</Flex>
										)}

										{insurance.phone && (
											<Flex align="center" gap="xs">
												<Icon
													name="Phone"
													size="xs"
													color="sand11"
													shrink={0}
												/>
												<Text size="sm" light>
													{insurance.phone}
												</Text>
											</Flex>
										)}

										<Flex align="center" gap="xs">
											<Icon
												name="Shield"
												size="xs"
												color={
													insurance.specialistRefsReqPriorAuth === true
														? "amber11"
														: insurance.specialistRefsReqPriorAuth === false
															? "green11"
															: "sand9"
												}
												shrink={0}
											/>
											<Text size="xs" light>
												{insurance.specialistRefsReqPriorAuth === true
													? "Referrals require prior auth"
													: insurance.specialistRefsReqPriorAuth === false
														? "Referrals don't require prior auth"
														: "Unknown if referrals require prior auth"}
											</Text>
										</Flex>
									</Flex>
								</Flex>
							);
						})}
					</Flex>
				) : (
					<Flex align="center" gap="xs">
						<Icon name="FileHeart" size="sm" color="sand11" shrink={0} />
						<Text size="sm" color="sand9" style={{ fontStyle: "italic" }}>
							No insurance records
						</Text>
					</Flex>
				)}
			</Flex>

			{/* Other tasks for this patient */}
			{otherTasks.length > 0 && (
				<Flex column gap="xs">
					<Text size="sm" weight="medium" color="sand11">
						Other Tasks
					</Text>
					<Flex column gap="xxs">
						{otherTasks.map((t) => (
							<Flex
								key={t.id}
								align="center"
								gap="xs"
								padding="xs"
								borderRadius="sm"
								background="sand2"
								style={{ cursor: "pointer" }}
								role="button"
								onClick={() => navigate(`/task/${t.id}`)}
							>
								<Icon name="FileText" size="sm" color="sand11" shrink={0} />
								<Text
									size="sm"
									color="sand12"
									style={{
										flex: 1,
										minWidth: 0,
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}
								>
									{t.title || "Untitled task"}
								</Text>
								<Text size="sm" color="sand11">
									{t.status}
								</Text>
							</Flex>
						))}
					</Flex>
				</Flex>
			)}

			<InsuranceCardImageModal
				images={imageModalData.images}
				isOpen={imageModalData.isOpen}
				onClose={() =>
					setImageModalData({ images: [], insuranceName: "", isOpen: false })
				}
				insuranceName={imageModalData.insuranceName}
			/>

			{editInsuranceModal.insurance && (
				<EditInsuranceModal
					insurance={editInsuranceModal.insurance}
					isOpen={editInsuranceModal.isOpen}
					onClose={() =>
						setEditInsuranceModal({
							insurance: null,
							isOpen: false,
							isSaving: false,
						})
					}
					onSave={handleSaveInsuranceEdit}
					isSaving={editInsuranceModal.isSaving}
					onDelete={
						editInsuranceModal.insurance.patientInsuranceId
							? () => {
									setShowDeleteConfirm(true);
									return Promise.resolve();
								}
							: undefined
					}
				/>
			)}

			<ConfirmDialog
				isOpen={showDeleteConfirm}
				title="Delete Insurance"
				message="Are you sure you want to delete this insurance?"
				confirmLabel="Delete"
				onConfirm={() => {
					setShowDeleteConfirm(false);
					void handleDeleteInsurance();
				}}
				onCancel={() => setShowDeleteConfirm(false)}
			/>

			{addInsuranceModal.isOpen && (
				<EditInsuranceModal
					insurance={{
						id: `tmp-${crypto.randomUUID()}`,
						planName: undefined,
						memberId: "",
						type: undefined,
						phone: undefined,
						address: undefined,
						payor: undefined,
						isPrimary: patient.insurance.length === 0,
						isExpired: false,
						specialistRefsReqPriorAuth: undefined,
						patientInsuranceId: undefined,
						imageUrls: undefined,
					}}
					isOpen={addInsuranceModal.isOpen}
					onClose={() =>
						setAddInsuranceModal({ isOpen: false, isSaving: false })
					}
					onSave={handleSaveInsuranceAdd}
					isSaving={addInsuranceModal.isSaving}
					title="Add Insurance"
				/>
			)}
		</Flex>
	);
};
